import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, ChevronDown, ChevronRight, ExternalLink,
  CheckCircle, Clock, AlertCircle, X, RefreshCw, Filter
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { LoadingBar } from '../components/shared/Skeletons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getMessageThemes(msg) {
  if (Array.isArray(msg.themes) && msg.themes.length > 0) return msg.themes;
  if (msg.theme) return [msg.theme];
  return ['uncategorized'];
}

function getPrimaryTheme(msg) {
  return getMessageThemes(msg)[0];
}

function formatAgo(date) {
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

function sentimentScore(s) {
  return { very_positive: 2, positive: 1, neutral: 0, negative: -1, very_negative: -2 }[s] ?? 0;
}

function avgSentimentKey(msgs) {
  if (!msgs.length) return 'neutral';
  const avg = msgs.reduce((s, m) => s + sentimentScore(m.sentiment), 0) / msgs.length;
  if (avg >= 1.5) return 'very_positive';
  if (avg >= 0.5) return 'positive';
  if (avg >= -0.5) return 'neutral';
  if (avg >= -1.5) return 'negative';
  return 'very_negative';
}

const SENT_CFG = {
  very_positive: { label: 'Very Positive', color: '#2e4d6e', bg: 'rgba(57,91,129,0.1)',  dot: '#395b81' },
  positive:      { label: 'Positive',      color: '#547a84', bg: 'rgba(84,122,132,0.1)', dot: '#547a84' },
  neutral:       { label: 'Neutral',       color: '#7a7a8a', bg: 'rgba(56,57,78,0.07)',  dot: '#9ca3af' },
  negative:      { label: 'Negative',      color: '#c06a55', bg: 'rgba(240,133,109,0.1)',dot: '#f0856d' },
  very_negative: { label: 'Very Negative', color: '#a0442e', bg: 'rgba(240,133,109,0.2)',dot: '#e06040' },
};

const CH_CFG = {
  whatsapp: { bg: 'rgba(84,122,132,0.12)', color: '#547a84' },
  sms:      { bg: 'rgba(57,91,129,0.12)',  color: '#395b81' },
  email:    { bg: 'rgba(56,57,78,0.08)',   color: '#38394e' },
  web:      { bg: 'rgba(240,133,109,0.12)',color: '#c06a55' },
};

const STATUS_CFG = {
  new:      { label: 'New',      color: '#c06a55', bg: 'rgba(240,133,109,0.1)', Icon: AlertCircle },
  reviewed: { label: 'Reviewed', color: '#395b81', bg: 'rgba(57,91,129,0.1)',  Icon: Clock       },
  replied:  { label: 'Replied',  color: '#547a84', bg: 'rgba(84,122,132,0.1)', Icon: CheckCircle },
};

// ─── BADGES ──────────────────────────────────────────────────────────────────

function SentBadge({ sentiment }) {
  const c = SENT_CFG[sentiment] || SENT_CFG.neutral;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: c.bg, color: c.color }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {c.label}
    </span>
  );
}

function ChBadge({ channel }) {
  const c = CH_CFG[channel] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: c.bg, color: c.color, textTransform: 'capitalize' }}>
      {channel}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.new;
  const Icon = cfg.Icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: cfg.bg, color: cfg.color }}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

// ─── MESSAGE ROW ─────────────────────────────────────────────────────────────

function MsgRow({ msg, checked, onCheck, navigate }) {
  const [expanded, setExpanded] = useState(false);
  const isNeg = ['negative', 'very_negative'].includes(msg.sentiment);

  return (
    <>
      <tr
        style={{
          background: checked
            ? 'rgba(240,133,109,0.06)'
            : expanded ? 'var(--accent,#f9fafb)' : 'transparent',
          borderBottom: expanded ? 'none' : '1px solid var(--border,#f0f0f0)',
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => { if (!checked && !expanded) e.currentTarget.style.background = 'var(--accent,#f9fafb)'; }}
        onMouseLeave={e => { if (!checked && !expanded) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Checkbox */}
        <td style={{ padding: '10px 8px 10px 16px', width: 36 }} onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onCheck(msg._id)}
            style={{ width: 15, height: 15, accentColor: '#f0856d', cursor: 'pointer' }}
          />
        </td>

        {/* Expand chevron */}
        <td style={{ padding: '10px 4px', width: 24, cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
          <ChevronRight
            size={13} color="#9ca3af"
            style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
          />
        </td>

        {/* Sender */}
        <td style={{ padding: '10px 12px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
          <span style={{ fontSize: 13, fontWeight: msg.status === 'new' ? 600 : 400, color: 'var(--foreground)' }}>
            {msg.senderContact}
          </span>
          {msg.senderName && (
            <span style={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>{msg.senderName}</span>
          )}
        </td>

        {/* Channel */}
        <td style={{ padding: '10px 12px' }}><ChBadge channel={msg.channel} /></td>

        {/* Sentiment */}
        <td style={{ padding: '10px 12px' }}>
          {msg.sentiment
            ? <SentBadge sentiment={msg.sentiment} />
            : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
        </td>

        {/* Status */}
        <td style={{ padding: '10px 12px' }}><StatusBadge status={msg.status} /></td>

        {/* Time */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {formatAgo(msg.receivedAt)}
        </td>

        {/* View button */}
        <td style={{ padding: '10px 14px 10px 8px', textAlign: 'right' }}>
          <button
            onClick={e => { e.stopPropagation(); navigate(`/respond/${msg._id}`); }}
            title="Open full detail"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 500, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border,#e5e7eb)', background: 'var(--background)', color: '#6b7280', cursor: 'pointer' }}
          >
            <ExternalLink size={11} /> View
          </button>
        </td>
      </tr>

      {/* Inline expanded preview */}
      {expanded && (
        <tr style={{ background: isNeg ? 'rgba(240,133,109,0.04)' : 'rgba(84,122,132,0.03)', borderBottom: '1px solid var(--border,#e5e7eb)' }}>
          <td colSpan={8} style={{ padding: '12px 14px 16px 56px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                  Original · {msg.originalLanguage || 'unknown'}
                </p>
                <p style={{ fontSize: 13, color: 'var(--foreground)', lineHeight: 1.65, margin: 0 }}>
                  {msg.originalText || '—'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                  Translated · English
                </p>
                <p style={{ fontSize: 13, color: 'var(--foreground)', lineHeight: 1.65, margin: 0 }}>
                  {msg.translatedText || msg.originalText || '—'}
                </p>
              </div>
            </div>
            {msg.themes?.length > 1 && (
              <div style={{ marginTop: 10, display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>All themes:</span>
                {msg.themes.map(t => (
                  <span key={t} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(57,91,129,0.1)', color: '#395b81', fontWeight: 500 }}>{t}</span>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── CATEGORY DETAIL PANEL ────────────────────────────────────────────────────

function CategoryPanel({ category, messages, selectedIds, onCheck, onSelectAll, onSelectNeg, navigate }) {
  const negMsgs = messages.filter(m => ['negative', 'very_negative'].includes(m.sentiment) && m.status !== 'replied');
  const otherMsgs = messages.filter(m => !['negative', 'very_negative'].includes(m.sentiment) || m.status === 'replied');

  const allIds = messages.map(m => m._id);
  const negIds = negMsgs.map(m => m._id);
  const allSel = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const negSel = negIds.length > 0 && negIds.every(id => selectedIds.has(id));
  const selCount = messages.filter(m => selectedIds.has(m._id)).length;

  return (
    <div style={{ borderTop: '1px solid var(--border,#e5e7eb)' }}>

      {/* Quick select bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--accent,#f9fafb)', borderBottom: '1px solid var(--border,#f0f0f0)' }}>
        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Select:</span>
        {negIds.length > 0 && (
          <button
            onClick={() => onSelectNeg(category)}
            style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, border: '1px solid #fecaca', background: negSel ? '#fee2e2' : '#fff', color: '#dc2626', cursor: 'pointer' }}
          >
            Negative only ({negIds.length})
          </button>
        )}
        <button
          onClick={() => onSelectAll(category)}
          style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, border: '1px solid var(--border,#e5e7eb)', background: allSel ? 'var(--accent,#f3f4f6)' : '#fff', color: '#6b7280', cursor: 'pointer' }}
        >
          All ({allIds.length})
        </button>
        {selCount > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#f0856d', marginLeft: 4 }}>
            {selCount} selected
          </span>
        )}
      </div>

      {/* Message table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--accent,#f9fafb)', borderBottom: '1px solid var(--border,#e5e7eb)' }}>
            <th style={{ width: 36, padding: '7px 8px 7px 16px' }} />
            <th style={{ width: 24, padding: '7px 4px' }} />
            {['Sender', 'Channel', 'Sentiment', 'Status', 'Received', ''].map((h, i) => (
              <th key={i} style={{ padding: '7px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>

          {/* Negative section */}
          {negMsgs.length > 0 && (
            <>
              <tr>
                <td colSpan={8} style={{ padding: '5px 16px', background: 'rgba(240,133,109,0.07)', borderBottom: '1px solid rgba(240,133,109,0.2)' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#c06a55', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <AlertCircle size={10} /> Needs attention ({negMsgs.length})
                  </span>
                </td>
              </tr>
              {negMsgs.map(msg => (
                <MsgRow key={msg._id} msg={msg} checked={selectedIds.has(msg._id)} onCheck={onCheck} navigate={navigate} />
              ))}
            </>
          )}

          {/* Neutral / positive section */}
          {otherMsgs.length > 0 && (
            <>
              {negMsgs.length > 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '5px 16px', background: 'rgba(84,122,132,0.07)', borderBottom: '1px solid rgba(84,122,132,0.2)', borderTop: '1px solid var(--border,#e5e7eb)' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#547a84', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Neutral / Positive ({otherMsgs.length})
                    </span>
                  </td>
                </tr>
              )}
              {otherMsgs.map(msg => (
                <MsgRow key={msg._id} msg={msg} checked={selectedIds.has(msg._id)} onCheck={onCheck} navigate={navigate} />
              ))}
            </>
          )}

        </tbody>
      </table>
    </div>
  );
}

// ─── STICKY COMPOSE BAR ───────────────────────────────────────────────────────

function ComposeBar({ selectedMsgs, adminName, onSent, onClear }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [sentReplyId, setSentReplyId] = useState(null);
  const navigate = useNavigate();

  const channels = useMemo(() => {
    const counts = {};
    selectedMsgs.forEach(m => { counts[m.channel] = (counts[m.channel] || 0) + 1; });
    return Object.entries(counts).map(([ch, n]) => `${n} ${ch}`).join(', ');
  }, [selectedMsgs]);

  const handleSend = async () => {
    if (!replyText.trim() || !selectedMsgs.length) return;
    setSending(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/bulk-reply`, {
        messageIds: selectedMsgs.map(m => m._id),
        replyText: replyText.trim(),
        adminName: adminName || 'Admin',
        theme: getPrimaryTheme(selectedMsgs[0]),
      });
      if (res.data.success) {
        const { sent, failed, replyId } = res.data.data || {};
        setSentCount(sent ?? selectedMsgs.length);
        setSentReplyId(replyId || null);
        setSent(true);
        if (failed > 0) {
          setError(`${failed} message${failed !== 1 ? 's' : ''} failed to send — check the detail page for errors.`);
        }
        onSent();
      } else {
        setError(res.data.error || 'Failed to send');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    setSent(false);
    setReplyText('');
    setError(null);
    setSentReplyId(null);
    onClear();
  };

  const handleViewReply = () => {
    navigate('/messages', { state: { tab: 'replies', expandReplyId: sentReplyId } });
  };

  if (sent) {
    return (
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#f0fdf4', borderTop: '2px solid #22c55e', boxShadow: '0 -4px 20px rgba(34,197,94,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 32px' }}>
          <CheckCircle size={18} color="#16a34a" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a', flex: 1 }}>
            Reply sent to {sentCount} recipient{sentCount !== 1 ? 's' : ''}
          </span>
          {sentReplyId && (
            <button
              onClick={handleViewReply}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1.5px solid #16a34a', background: '#fff', color: '#16a34a', cursor: 'pointer' }}
            >
              <ExternalLink size={13} /> View reply
            </button>
          )}
          <button
            onClick={handleDismiss}
            title="Dismiss"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid #bbf7d0', background: '#fff', color: '#16a34a', cursor: 'pointer', flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>
        {error && (
          <p style={{ fontSize: 12, color: '#d97706', padding: '0 32px 10px', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertCircle size={12} /> {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: 'var(--card,#fff)', borderTop: '2px solid #f0856d', boxShadow: '0 -4px 20px rgba(240,133,109,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 32px' }}>

        {/* Selection info */}
        <div style={{ flexShrink: 0, minWidth: 140 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#f0856d', margin: 0 }}>
            {selectedMsgs.length} selected
          </p>
          <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>{channels}</p>
        </div>

        {/* Textarea */}
        <textarea
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          placeholder="Write your reply… auto-translated into each recipient's language"
          rows={2}
          autoFocus
          style={{ flex: 1, padding: '8px 12px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border,#e5e7eb)', background: 'var(--background)', color: 'var(--foreground)', fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: 1.5 }}
          onFocus={e => e.target.style.borderColor = '#f0856d'}
          onBlur={e => e.target.style.borderColor = 'var(--border,#e5e7eb)'}
          disabled={sending}
        />

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          <button
            onClick={handleSend}
            disabled={!replyText.trim() || sending}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', whiteSpace: 'nowrap', transition: 'background 0.15s', cursor: !replyText.trim() || sending ? 'not-allowed' : 'pointer', background: !replyText.trim() || sending ? '#e5e7eb' : '#f0856d', color: !replyText.trim() || sending ? '#9ca3af' : '#fff' }}
          >
            <Send size={13} />
            {sending ? 'Sending…' : `Send to ${selectedMsgs.length}`}
          </button>
          <button
            onClick={onClear}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '5px 8px', fontSize: 11, fontWeight: 500, borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}
          >
            <X size={11} /> Clear selection
          </button>
        </div>
      </div>

      {error && (
        <p style={{ fontSize: 12, color: '#dc2626', padding: '0 32px 10px', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function Respond() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [channelFilter, setChannelFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [openCategory, setOpenCategory] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const adminName = 'Admin';
  const navigate = useNavigate();

  const fetchMessages = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/messages`);
      if (res.data.success) setMessages(res.data.messages);
      else throw new Error(res.data.message);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const filtered = useMemo(() => {
    let f = [...messages];
    if (statusFilter === 'pending') f = f.filter(m => m.status !== 'replied');
    if (statusFilter === 'replied') f = f.filter(m => m.status === 'replied');
    if (channelFilter !== 'all') f = f.filter(m => m.channel === channelFilter);
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(m =>
        m.senderContact?.toLowerCase().includes(q) ||
        m.translatedText?.toLowerCase().includes(q) ||
        m.originalText?.toLowerCase().includes(q)
      );
    }
    return f;
  }, [messages, statusFilter, channelFilter, search]);

  // Group by primary theme, most negative messages first
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(m => {
      const t = getPrimaryTheme(m);
      if (!g[t]) g[t] = [];
      g[t].push(m);
    });
    return Object.entries(g).sort((a, b) => {
      const aNeg = a[1].filter(m => ['negative', 'very_negative'].includes(m.sentiment) && m.status !== 'replied').length;
      const bNeg = b[1].filter(m => ['negative', 'very_negative'].includes(m.sentiment) && m.status !== 'replied').length;
      return bNeg - aNeg;
    });
  }, [filtered]);

  const handleCheck = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const handleSelectAll = useCallback((category) => {
    const msgs = grouped.find(([c]) => c === category)?.[1] || [];
    const ids = msgs.map(m => m._id);
    const allSel = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      allSel ? ids.forEach(id => n.delete(id)) : ids.forEach(id => n.add(id));
      return n;
    });
  }, [grouped, selectedIds]);

  const handleSelectNeg = useCallback((category) => {
    const msgs = grouped.find(([c]) => c === category)?.[1] || [];
    const negIds = msgs
      .filter(m => ['negative', 'very_negative'].includes(m.sentiment) && m.status !== 'replied')
      .map(m => m._id);
    const allSel = negIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      allSel ? negIds.forEach(id => n.delete(id)) : negIds.forEach(id => n.add(id));
      return n;
    });
  }, [grouped, selectedIds]);

  const selectedMsgs = filtered.filter(m => selectedIds.has(m._id));

  return (
    <DashboardLayout>
      <LoadingBar isLoading={loading} />
      <div style={{ maxWidth: 1100, paddingBottom: selectedIds.size > 0 ? 130 : 0 }}>

        {/* ── HEADER ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--foreground)', marginBottom: 4 }}>Respond</h1>
            <p style={{ fontSize: 14, color: 'var(--muted-foreground,#6b7280)' }}>
              Select messages to reply — negative messages are flagged at the top of each category.
            </p>
          </div>
          <button
            onClick={fetchMessages}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid var(--border,#e5e7eb)', background: 'var(--background)', color: 'var(--muted-foreground,#6b7280)', cursor: 'pointer' }}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* ── FILTERS ── */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', padding: '12px 16px', background: 'var(--card,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 10 }}>
          <Filter size={13} color="#9ca3af" />

          <div style={{ display: 'flex', border: '1px solid var(--border,#e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
            {[{ v: 'pending', l: 'Pending' }, { v: 'all', l: 'All' }, { v: 'replied', l: 'Replied' }].map(opt => (
              <button key={opt.v} onClick={() => setStatusFilter(opt.v)} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 500, border: 'none', borderLeft: opt.v !== 'pending' ? '1px solid var(--border,#e5e7eb)' : 'none', cursor: 'pointer', transition: 'all 0.1s', background: statusFilter === opt.v ? '#f0856d' : 'transparent', color: statusFilter === opt.v ? '#fff' : 'var(--muted-foreground,#6b7280)' }}>
                {opt.l}
              </button>
            ))}
          </div>

          <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)} style={{ padding: '5px 10px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border,#e5e7eb)', background: 'var(--background)', color: 'var(--foreground)', cursor: 'pointer' }}>
            <option value="all">All channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
            <option value="email">Email</option>
            <option value="web">Web</option>
          </select>

          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search sender or message…"
            style={{ flex: 1, minWidth: 200, padding: '5px 12px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border,#e5e7eb)', background: 'var(--background)', color: 'var(--foreground)', outline: 'none' }}
          />

          {(search || channelFilter !== 'all') && (
            <button onClick={() => { setSearch(''); setChannelFilter('all'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 12, fontWeight: 500, borderRadius: 8, border: 'none', background: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}>
              <X size={11} /> Clear
            </button>
          )}
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            Failed to load messages: {error}
          </div>
        )}

        {/* ── LOADING ── */}
        {loading && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ height: 56, background: i % 2 ? 'var(--accent,#f9fafb)' : 'var(--card)', borderBottom: '1px solid var(--border,#e5e7eb)' }} />
            ))}
          </div>
        )}

        {/* ── EMPTY ── */}
        {!loading && grouped.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 32px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle size={28} color="#16a34a" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--foreground)', marginBottom: 6 }}>
              {statusFilter === 'pending' ? 'All caught up!' : 'No messages found'}
            </h3>
            <p style={{ fontSize: 14, color: '#9ca3af' }}>
              {statusFilter === 'pending' ? 'No pending messages need a reply.' : 'No messages match your filters.'}
            </p>
          </div>
        )}

        {/* ── SUMMARY TABLE + EXPANDABLE PANELS ── */}
        {!loading && grouped.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 180px 110px 120px 140px', alignItems: 'center', padding: '0', background: 'var(--accent,#f9fafb)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px' }} />
              {['Category', 'Avg Sentiment', 'Messages', 'Unresponded', 'Action'].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', borderLeft: '1px solid var(--border,#e5e7eb)' }}>{h}</div>
              ))}
            </div>

            {/* Category cards */}
            {grouped.map(([cat, msgs]) => {
              const pending = msgs.filter(m => m.status !== 'replied').length;
              const avgSent = avgSentimentKey(msgs);
              const isOpen = openCategory === cat;
              const negCount = msgs.filter(m => ['negative', 'very_negative'].includes(m.sentiment) && m.status !== 'replied').length;

              return (
                <div
                  key={cat}
                  style={{
                    border: isOpen ? '2px solid #f0856d' : '1px solid var(--border,#e5e7eb)',
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: 'var(--card,#fff)',
                    transition: 'border-color 0.15s',
                  }}
                >
                  {/* Summary row */}
                  <div
                    onClick={() => setOpenCategory(prev => prev === cat ? null : cat)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr 180px 110px 120px 140px',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: isOpen ? 'rgba(240,133,109,0.05)' : 'var(--card,#fff)',
                      transition: 'background 0.1s',
                      borderLeft: `3px solid ${negCount > 0 ? '#ef4444' : pending > 0 ? '#f59e0b' : '#22c55e'}`,
                    }}
                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--accent,#f9fafb)'; }}
                    onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = isOpen ? '#f5f3ff' : 'var(--card,#fff)'; }}
                  >
                    <div style={{ padding: '14px 8px 14px 12px' }}>
                      <ChevronDown size={15} color={isOpen ? '#f0856d' : '#9ca3af'} style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                    </div>
                    <div style={{ padding: '14px 16px', borderLeft: '1px solid var(--border,#e5e7eb)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--foreground)', textTransform: 'capitalize' }}>{cat}</span>
                      {negCount > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(240,133,109,0.12)', color: '#c06a55' }}>
                          {negCount} negative
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '14px 16px', borderLeft: '1px solid var(--border,#e5e7eb)' }}><SentBadge sentiment={avgSent} /></div>
                    <div style={{ padding: '14px 16px', borderLeft: '1px solid var(--border,#e5e7eb)', fontSize: 14, fontWeight: 700, color: 'var(--foreground)' }}>{msgs.length}</div>
                    <div style={{ padding: '14px 16px', borderLeft: '1px solid var(--border,#e5e7eb)' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: pending > 0 ? '#c06a55' : '#547a84' }}>{pending}</span>
                    </div>
                    <div style={{ padding: '14px 16px', borderLeft: '1px solid var(--border,#e5e7eb)' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenCategory(prev => prev === cat ? null : cat)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', background: isOpen ? '#d4735e' : '#f0856d', color: '#fff', cursor: 'pointer', transition: 'background 0.15s' }}
                      >
                        {isOpen ? 'Close' : 'View & Reply'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {isOpen && (
                    <div style={{ borderTop: '2px solid #f0856d' }}>
                      <CategoryPanel
                        category={cat}
                        messages={msgs}
                        selectedIds={selectedIds}
                        onCheck={handleCheck}
                        onSelectAll={handleSelectAll}
                        onSelectNeg={handleSelectNeg}
                        navigate={navigate}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── STICKY COMPOSE BAR ── */}
      {selectedIds.size > 0 && (
        <ComposeBar
          selectedMsgs={selectedMsgs}
          adminName={adminName}
          onSent={fetchMessages}
          onClear={() => setSelectedIds(new Set())}
        />
      )}
    </DashboardLayout>
  );
}